/**
 * Thin client for the shared xplatform **core** service. Used by the job
 * FilterPanel to search users in the caller's organisation (the backend
 * scopes results to the org from the BeeHive token — the frontend sends
 * no org id). Mirrors the extraction frontend's `xplatformCoreApi`.
 */
import { HttpClient } from "@utils/httpClient";
import { buildUserSearchResultViewModel } from "@utils/builders/user";
import type { IUserSearchResultModel, IUserSearchResultViewModel } from "@models/user";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL;

if (!BASE_URL) {
  // Warn rather than throw: the dashboard still works without user search
  // (the autocomplete simply returns no matches) so a missing core URL
  // must not take the whole mapping app down.
  console.warn(
    "VITE_XPLATFORM_CORE_API_BASE_URL is not set. The job filter's user " +
      "search will return no results until it is configured in .env.",
  );
}

class XPlatformCoreApi {
  private httpClient: HttpClient;

  constructor(httpClient?: HttpClient) {
    this.httpClient = httpClient ?? new HttpClient(BASE_URL ?? "");
  }

  /**
   * Search users in the caller's organisation by email or name. Returns up
   * to `limit` matches (server caps at 50). The backend requires a query of
   * at least 2 characters; callers should debounce and guard short input.
   */
  async searchUsers(
    query: string,
    limit: number = 10,
  ): Promise<IUserSearchResultViewModel[]> {
    const params = new URLSearchParams({ q: query, limit: limit.toString() });
    const results = await this.httpClient.makeAuthenticatedRequest<
      IUserSearchResultModel[]
    >(`/users/search?${params.toString()}`, { method: "GET" });
    return results.map(buildUserSearchResultViewModel);
  }
}

export { XPlatformCoreApi };

export const xplatformCoreApi = new XPlatformCoreApi();
